import { Component, OnInit, viewChildren } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd } from '@angular/router';
import {
    APP_CONFIG,
    PageType,
    pickContrastingTextColor,
    requiresContrastingTextColor,
    ZoneType,
} from '@gallaghercp/core-frontend';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { Observable, tap } from 'rxjs';
import { HeaderComponent } from '../../header/header.component';

@Component({
    selector: 'core-top-navigation',
    templateUrl: './top-nav.component.html',
    standalone: false,
    host: {
        '(window:resize)': 'onResize($event)',
        '(keydown.escape)': 'onEsc()',
    },
})
export class TopNavComponent extends HeaderComponent implements OnInit {
    readonly dropdowns = viewChildren(BsDropdownDirective);

    bgHeader?: string;
    bgNav?: string;
    linkColorVar?: string;
    activeColorVar?: string;

    pillars$!: Observable<any>;
    portalLinkTarget?: string;

    displayLoggedInUser: boolean = !!this.config.get('auth')?.displayLoggedInUser;
    activeMenuTab = new FormControl(0);
    fragment?: string;
    resizeTimeout: any;
    clickIndexNo?: void | number;
    navigationOrder: 'name' | 'displayOrder' = (this.config.get('navigation') as any)?.navigationOrder ?? 'name';
    isSplashLandingConfigEnabled = !!this.config.get('splashLandingPage');
    isSplashLandingNavigation = this.config.get('splashLandingPage')
        ? (this.config.get('splashLandingPage') as any)['isNavigation']
        : false;
    isSplashLandingSearch = this.config.get('splashLandingPage')
        ? (this.config.get('splashLandingPage') as any)['isSearchEnabled']
        : true;
    isSplashLandingCurrentPage: boolean = false;
    hideNavigationOnSplashLanding: boolean = false;
    hideMobileNavigationOnSplashLanding: boolean = false;
    isPortalLinkHidden = this.config.get('splashLandingPage')
        ? (this.config.get('splashLandingPage') as any)['isPortalLinkHidden']
        : false;
    isSplashLandingMobileNavigation = this.config.get('splashLandingPage')
        ? (this.config.get('splashLandingPage') as any)['isMobileNavigationHidden']
        : false;
    menuStyle = (this.config.get('navigation') as any)?.style || 'default';
    navBreakpoint = (this.config.get('navigation') as any)?.breakpoint || 1088;
    customContactButtonEnabled: boolean = this.config.get('customContactButton')?.enabled ?? false;
    customContactButtonTarget: boolean = this.config.get('customContactButton')?.target ?? false;
    customContactButtonIcon: boolean = (this.config.get('customContactButton') as any)?.hasLockIcon ?? false;
    customContactButtonPath: string = this.config.get('customContactButton')?.path ?? '';
    customContactButtonText: string = this.config.get('customContactButton')?.text ?? '';
    // Prefer button colors from global colors config. Expose with the same syntax as other customContactButton fields
    // to keep consistency and avoid inline style expressions in the template.
    customContactButtonActionBg: string | undefined =
        this.config.get('colors')?.actionBg ?? this.config.get('colors')?.secondary;
    customContactButtonActionText: string | undefined = this.config.get('colors')?.actionText ?? '#ffffff';
    showLogoutButton: boolean = (this.config.get('navigation') as any)?.showLogoutButton ?? true;
    customSearchIcon: string | undefined = this.config.get('search')?.customSearchIcon;
    customCloseIcon: string | undefined = this.config.get('search')?.customCloseIcon;
    closeButtonSize: string | undefined = this.config.get('search')?.closeButtonSize;
    noResultPreview: boolean = !!this.config.get('search')?.noResultPreview;
    customBurgerIcon: string | undefined =
        this.config.get('theme')?.navigation?.customBurgerIcon ||
        (this.config.get('navigation') as any)?.customBurgerIcon;

    // Menu variables
    content$?: Observable<any>;
    openDropdown: any;
    selectedMobNavLinkIndex: number | undefined;
    isDesktop: boolean = true;
    isCategoryNav?: boolean;
    resetMobileScroll?: boolean;
    isMobileNavFooter: boolean = true; // Boolean to use a fixed footer element for cta at bottom of mobile nav
    contactEnabled!: boolean;
    dropdownIsOpen!: boolean;

    protected readonly requiresContrastingTextColor = requiresContrastingTextColor;

    // private readonly renderer = inject(Renderer2);

    get portalLink(): string {
        const link = this.config.get('portalLink') ?? '';
        if (link.includes('{{currentZoneRoute}}')) {
            const currentZone = this.zoneService.getCurrentZone() as ZoneType;
            if (currentZone?.code) {
                return link.replace('{{currentZoneRoute}}', currentZone.code);
            }
            return link.replace('{{currentZoneRoute}}', '');
        }
        return link;
    }

    get homeRouterLink(): PageType | null {
        try {
            const currentZone = this.zoneService.getCurrentZone() as ZoneType;

            // Only apply dynamic home link logic if zoneRouting exists in app.config
            const zr = this.config.get('zoneRouting');
            if (!Array.isArray(zr) || zr.length === 0) {
                return null;
            }

            const zoneRouting: any[] = (zr as any[]).filter(z => z && z.id != null && z.route);

            // If we have a current zone with an id, try to map it to a configured route
            if (currentZone && typeof (currentZone as any).zone === 'number') {
                const match = zoneRouting.find(z => z.id === (currentZone as any).zone);
                if (match && match.route) {
                    // Copy match object to avoid mutating the original object
                    const matchCopy = { ...match };
                    // Add url, as it's used in the navigation method
                    matchCopy.url = match.route;
                    return matchCopy;
                }
            }
        } catch {
            /* empty */
        }
        return null;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.setPortalLinkTarget();
        this.updateBg();
        this.setPortalLinkTarget();

        this.content$ = this.client.getPageCategories();

        if (this.isBrowser) {
            this.isDesktopCheck(this.windowService.nativeWindow);
        }

        if (this.isSplashLandingConfigEnabled) {
            // Only get's run if the splashLandingPage object is present in app.config
            this.updateSplashLandingNavigationState();
        }

        this.subscriptions.push(
            this.store
                .select(state => state.app)
                .subscribe(() => {
                    this.updateBg();
                }),
            this.settings.isTransparentNavigation().subscribe((state: any) => {
                this.isTransparentNav = state;
                this.updateBg();
            }),
            this.api.zoneChangeEmit.subscribe(() => {
                this.setPortalLinkTarget();
                // Only run the below logic if the splash landing page is enabled in app.config as won't be needed for most Bedrocc sites.
                if (this.isSplashLandingConfigEnabled) {
                    this.updateSplashLandingNavigationState();
                }
                this.pillars$ = this.client.getPillars();
            }),
            this.router.events.subscribe((event: any) => {
                if (event instanceof NavigationEnd) {
                    this.updateLogoUrl();
                    if (this.isSplashLandingConfigEnabled) {
                        this.updateSplashLandingNavigationState();
                    }
                }
            }),
            this.translateProvider.onLangChange.subscribe(() => {
                this.pillars$ = this.client.getPillars();
            }),
            this.settings.showBenefitCategories().subscribe((state: any) => {
                this.isCategoryNav = state;
            }),
            this.settings.isContactEnabled().subscribe((state: any) => {
                this.contactEnabled = state;
            }),
            this.pages$!.pipe(
                tap((pages: PageType[]) => {
                    if (pages) {
                        (pages ?? []).map((page: PageType & { isNested?: boolean }) => {
                            (page.children ?? []).map((child: PageType) => {
                                if ((child.children ?? []).length) {
                                    page.isNested = true;
                                }
                            });
                        });
                    }
                }),
            ).subscribe(),
        );
    }

    private updateSplashLandingNavigationState(): void {
        const isHome =
            this.router.url === '/' || (this.isBrowser && this.windowService.nativeWindow?.location.pathname === '/');
        this.isSplashLandingCurrentPage = isHome && this.isZoneRouteOne;
        this.hideNavigationOnSplashLanding = !this.isSplashLandingNavigation;
        this.hideMobileNavigationOnSplashLanding = !this.isSplashLandingMobileNavigation;

        if (this.isBrowser) {
            // Ensure searchEnabled, hideNavigationOnSplashLanding and isPortalLinkHidden are updated based on the current state
            if (this.isSplashLandingCurrentPage) {
                this.searchEnabled = this.isSplashLandingSearch;
                this.hideNavigationOnSplashLanding = !this.isSplashLandingNavigation;
                this.hideMobileNavigationOnSplashLanding = !this.isSplashLandingMobileNavigation;
                this.isPortalLinkHidden = this.config.get('splashLandingPage')?.isPortalLinkHidden ?? false;
            } else {
                this.searchEnabled = this.config.get('showSearch') !== false; // Fallback to config
                this.hideNavigationOnSplashLanding = false;
                this.hideMobileNavigationOnSplashLanding = false;
                this.isPortalLinkHidden = false;
            }
        }
    }

    get loggedInUserName(): string {
        const metadata: string[] | undefined = this.config.get('auth')?.loggedInUserMetadata;
        if (Array.isArray(metadata) && metadata.length > 0 && this.user) {
            return metadata
                .map(key => this.user[key] || this.user.data?.[key])
                .filter(val => !!val)
                .join(' ');
        }
        return this.user?.username || '';
    }

    // Helper to derive the list of child pages shown in the mobile dropdown.
    // For category nav "Benefits", flatten all pillar categories into a single list.
    getMobileNavPages(page: PageType, content: any[] | null): PageType[] {
        if (this.isCategoryNav && page.url === 'benefits' && content && content.length > 0) {
            // Extract all categorized pages from all pillars
            const allCategorizedPages: PageType[] = [];
            const pageUrls = new Set<string>();
            content.forEach((pillar: any) => {
                if (pillar.pageCategories) {
                    pillar.pageCategories.forEach((categorizedPage: PageType) => {
                        const uniqueKey = categorizedPage.url || String(categorizedPage.id);
                        if (!pageUrls.has(uniqueKey) && this.showInMenu(categorizedPage, 'no-child')) {
                            pageUrls.add(uniqueKey);
                            allCategorizedPages.push(categorizedPage);
                        }
                    });
                }
            });
            return allCategorizedPages;
        }
        return page.children || [];
    }

    // Helper to identify the first nav item labelled "Home" (case-insensitive)
    isHomeNavItem(page: PageType | undefined | null): boolean {
        // Check page is defined to avoid TS error
        if (!page) return false;

        const label = (page as any)?.meta?.friendlyPageName ?? (page as any)?.name ?? '';
        return typeof label === 'string' && label.toLowerCase() === 'home';
    }

    // Centralized click handler to avoid duplicating <a> markup in the template
    onNavItemClick(_index: number, page: PageType, event: Event): void {
        event.preventDefault();
        if (this.isHomeNavItem(page) && this.homeRouterLink !== null) {
            // Home item relies on [routerLink]; no manual navigation
            this.navigateToPage([this.homeRouterLink]);
            return;
        }
        this.navigateToPage([page]);
    }

    get isNavbarWithFlyout(): boolean | undefined {
        return (this.menuStyle === 'flyout' && !this.isIndividualMenuFlyout) || this.isIndividualMenuFlyout;
    }

    onResize(event: Event) {
        if (this.isBrowser) {
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = setTimeout(() => {
                this.isDesktopCheck(event.target);
            }, 200);
        }
    }

    onEsc() {
        const dropdowns = this.dropdowns();
        if (this.dropdownIsOpen && dropdowns) {
            dropdowns.forEach((dropdown: BsDropdownDirective) => {
                this.openDropdown = dropdown;
                if (this.openDropdown._elementRef.nativeElement.classList.contains('show')) {
                    this.openDropdown._elementRef.nativeElement.firstChild.focus();
                }
                this.openDropdown.hide();
            });
        }
    }

    private updateBg(): void {
        const colors = this.uiBranding.getBrandColors();
        const configColors = (this.config.get('colors') ?? (APP_CONFIG as any).colors) as any;

        const headerBg = colors?.bg ?? configColors?.elementBackgroundColour ?? configColors?.headerBg;

        const navigationBg =
            configColors?.navBg ?? colors?.bg ?? configColors?.elementBackgroundColour ?? configColors?.headerBg;

        if (headerBg) {
            this.bgHeader = this.isTransparentNav ? 'transparent' : headerBg;
        } else {
            this.bgHeader = this.isTransparentNav ? 'transparent' : undefined;
        }

        if (navigationBg) {
            // Link colors should be determined by background contrast
            const contrastColor = pickContrastingTextColor(navigationBg);
            const navLink = contrastColor === 'white' ? '#ffffff' : '#000000';
            const navActive = navLink;

            this.bgNav = this.isTransparentNav ? 'transparent' : navigationBg;
            this.linkColorVar = navLink;
            this.activeColorVar = navActive;
        } else {
            this.bgNav = this.isTransparentNav ? 'transparent' : undefined;
            this.linkColorVar = undefined;
            this.activeColorVar = undefined;
        }

        // Set a dedicated icon color CSS variable based on the ACTUAL header background
        // Prefer the computed header background when nav is solid; otherwise fall back to navigationBg.
        // Always write a safe default if no base color is available, so CSS var is defined.
        try {
            const baseForIcons: string | undefined =
                this.bgHeader && this.bgHeader !== 'transparent' ? this.bgHeader : navigationBg;
            let iconColor = '#000000';
            if (baseForIcons) {
                const iconContrast = pickContrastingTextColor(baseForIcons);
                iconColor = iconContrast === 'white' ? '#ffffff' : '#000000';
            }
            this.documentService.documentElement?.style.setProperty('--color-nav-icon', iconColor);

            const mobileBgColor = (this.config.get('navigation') as any)?.mobileBgcolor;
            if (mobileBgColor) {
                this.documentService.documentElement?.style.setProperty('--mobile-bg-color', mobileBgColor);
                this.documentService.documentElement?.style.setProperty('--color-banner-bg', mobileBgColor);
            }
        } catch {
            /* noop: document may not be available on server */
        }

        this.updateLogoUrl();
    }

    onOpenChange(isOpen: boolean, index: number): void {
        const overlayElem = this.documentService.querySelector('.page-overlay');

        if (isOpen && overlayElem) {
            this.renderer.addClass(overlayElem, 'page-overlay--is-active');
            this.clickIndexNo = index;
        } else if (this.clickIndexNo === index) {
            // only close page overlay when the same link is clicked twice
            this.renderer.removeClass(overlayElem, 'page-overlay--is-active');
        }
    }

    onDropdownOpen() {
        return (this.dropdownIsOpen = true);
    }

    menuStateCheck(menuState: boolean, event: Event) {
        this.closeChildNav(event);
        if (this.dropdowns()) {
            this.hideDropdowns();
        }
        if (this.documentService.body) {
            this.documentService.body.style.setProperty('overflow-y', menuState ? 'revert' : 'hidden', 'important');
        }
    }

    toggleMobileMenu(evt: Event) {
        this.isCollapsed = !this.isCollapsed;
        this.menuStateCheck(this.isCollapsed, evt);

        // No need to re-apply search icon colour here; it is bound via CSS variable
    }

    closeChildNav(event?: Event) {
        // Setting value to number that isn't the index to remove is-active class on mobile benefits submenu
        this.selectedMobNavLinkIndex = 99;
        // Preventing click propagation. This keeps the mobile Benefits accordion open when a submenu is closed
        if (event) {
            event.stopPropagation();
        }
        // Add main scrollbar back if required
        this.resetMobileScroll = false;
    }

    setTopNavActiveLinkState(page: any) {
        let setActiveState = false;
        // If page has children and child page is current page, set active link on parent page link
        if (page.children && page.children.length > 0) {
            for (const child of page.children) {
                const childPageRoute = '/' + child.url;
                if (this.router.url === childPageRoute) {
                    setActiveState = true;
                }
            }
        } else {
            // No child pages
            // Home page check as this.router.url and page.url won't match
            if (this.router.url === '/' && page.url === 'default') {
                setActiveState = true;
            } else {
                // Set link to active if link page and current page match for page that aren't home and don't have children
                const pageRoute = '/' + page.url;
                setActiveState = this.router.url === pageRoute;
            }
        }
        return setActiveState;
    }

    setDropdownActiveLinkState(page: any) {
        // Set dropdown (child) link to active if link page and current page match
        const pageRoute = '/' + page.url;
        return this.router.url === pageRoute;
    }

    isDesktopCheck(elem: any) {
        if (elem.innerWidth >= this.navBreakpoint) {
            if (!this.isCollapsed) {
                this.isCollapsed = true;
            }
            this.isDesktop = true;
        } else {
            this.isDesktop = false;
        }

        this.setMobChildNavHeight();
    }

    setMobChildNavHeight() {
        const offset = this.isMobileNavFooter ? 374 : 320;
        const vh = this.windowService.innerHeight - offset;
        this.documentService.documentElement?.style.setProperty('--vhcustom-child-menu', `${vh}px`);
    }

    navigateToPage(pages: PageType[]) {
        if (this.dropdowns()) {
            this.hideDropdowns();
            this.onOpenChange(false, -1);
        }
        if (this.documentService.body) {
            this.documentService.body.style.overflowY = 'revert';
        }
        if (!this.isCollapsed) {
            this.isCollapsed = true;
        }

        // Get zone url path to add in later
        // Add current tag back in
        let zoneUrl = '';
        const zoneRouting = this.config.get('zoneRouting');
        if (
            Array.isArray(zoneRouting) &&
            zoneRouting.length &&
            this.zoneService.getCurrentZone() &&
            (this.zoneService.getCurrentZone() as ZoneType).zone > 1
        ) {
            zoneUrl = (this.zoneService.getCurrentZone() as ZoneType).code + '/';
        }

        if (pages.length === 1) {
            // Handling of non-nested routes
            const currentPage = pages[0] ?? ({} as PageType);
            if (currentPage && currentPage.url.indexOf('#') > -1) {
                // Contains #
                const urlArray = currentPage.url.split('#');
                this.router.navigate([urlArray[0]], { fragment: urlArray[1] });
                return;
            }

            // Handling for when we're on a default zone url, where zoneUrl and currentPage.url are the same
            if (zoneUrl === currentPage.url + '/') {
                this.router.navigate(['/' + currentPage.url]);
                return;
            }

            if (currentPage.url === 'default') {
                currentPage.url = '';
            }

            // Strip configured zone route prefix from the beginning of the URL (driven by app.config zoneRouting)
            currentPage.url = this.stripConfiguredZonePrefix(currentPage.url);
            this.router.navigate(['/' + (currentPage.url.length > 1 ? zoneUrl : '') + currentPage.url]);
        } else {
            // Handling of non-nested routes
            // Strip configured zone route prefixes out for each nested segment (zoneRouting)
            const nestedUrls = pages.map((page: PageType) => this.stripConfiguredZonePrefix(page.url));
            const fullUrl = nestedUrls.join('/');
            // If we don't care about zones this still works and zoneUrl is empty
            this.router.navigate(['/' + zoneUrl + fullUrl]);
        }
    }

    closeChildDropdown(event?: Event) {
        // Only close on explicit activation (click or Enter/Space). Do not close on Tab navigation.
        if (event instanceof KeyboardEvent) {
            const key = event.key;
            // Allow close only for Enter or Space keys
            if (key !== 'Enter' && key !== ' ') {
                return;
            }
            // Prevent page scroll on Space
            event.preventDefault();
        }
        if (this.dropdowns()) {
            this.hideDropdowns();
            this.onOpenChange(false, -1);
        }
    }

    stopPropagation($event: MouseEvent | KeyboardEvent | TouchEvent | Event) {
        return $event.stopPropagation();
    }

    hideDropdowns() {
        this.dropdowns()?.forEach((dropdown: BsDropdownDirective) => {
            dropdown.hide();
        });
    }

    private setPortalLinkTarget(): void {
        if (!this.portalLink) {
            this.portalLinkTarget = undefined;
            return;
        }

        try {
            const portalUrl = new URL(this.portalLink, this.windowService.location?.origin);
            const currentHost = this.windowService.location?.host;

            this.portalLinkTarget = portalUrl.host !== currentHost ? '_blank' : undefined;
        } catch {
            this.portalLinkTarget = undefined;
        }
    }

    private stripConfiguredZonePrefix(url: string): string {
        if (!url) return url;

        // Use routes from app.config zoneRouting
        const zoneRouting = this.config.get('zoneRouting');
        const routes: string[] = ((Array.isArray(zoneRouting) ? zoneRouting : []) as any[])
            .map(z => z?.route)
            .filter((r: any) => !!r) as string[];

        if (!routes.length) return url;

        const result = url;
        for (const r of routes) {
            if (!r) continue;
            const normalized = String(r).replace(/^\/+|\/+$/g, '');

            // Handle '/normalized/...'
            if (result.startsWith(`/${normalized}/`)) {
                return result.substring(normalized.length + 2);
            }
            // Handle 'normalized/...'
            if (result.startsWith(`${normalized}/`)) {
                return result.substring(normalized.length + 1);
            }
            // Handle exact match '/normalized' or 'normalized'
            if (result === `/${normalized}` || result === normalized) {
                return '';
            }
        }
        return result;
    }

    protected showMobileNavFooter() {
        const showMobileNavFooter = this.isMobileNavFooter;

        // Add additional OR (i.e. `||`) gates for additional mobile nav footer features
        const showMobileNavFooterFeatures = this.contactEnabled || this.customContactButtonEnabled;

        return showMobileNavFooter && showMobileNavFooterFeatures;
    }
}
